import { DateAxis, LineSeries, ValueAxis, XYChart, Legend } from "@amcharts/amcharts4/charts";
import { color, create, Container, percent, registry } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { SOV_MIX_COLORS } from "../../../constants";
import { disposeChart } from "../helpers/chartHelpers";
import { configureDateAxis, configureValueAxis, lineSeriesConfiguration } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import {
  dateAxisLabelFormatter_MonthlyFY,
  dateAxisLabelFormatter_QuarterlyFY,
  dateAxisLabelFormatter_YearlyFY,
  dateChinaAxisLabelFormatter_QuarterlyFY,
  dateChinaAxisLabelFormatter_YearlyFY,
  formatChinaDateAxisLabelToQuarterlyFY,
  formatChinaDateAxisLabelToYearlyFY,
  formatDateAxisLabelToQuarterlyFY,
  formatDateAxisLabelToYearlyFY,
} from "../helpers/pmoConfigurations";
import { ThemeInt } from "../helpers/types";
import { MetricType } from "../../../pages/BrandPerformanceDashboard/components/BrandKPITrend/BrandKPIContent";

interface Props {
  data: any;
  granularity: string;
  theme: ThemeInt;
  chartId: string;
  seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
  market: string;
  diff: number;
  metric: string;
  maximumScoreRange: number;
  chartTitles: MetricType[];
}

class BrandKPITrendChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chartId = this.props.chartId;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId);
      this.initChart();
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const {
      data,
      granularity,
      theme,
      seriesList,
      percentageChart,
      nissanCompetitor,
      market,
      diff,
      maximumScoreRange = 100,
      chartTitles,
    } = this.props;

    const metricCharts = chartTitles || ["OaO", "Innovative", "Excitement", "EV TOM", "AD TOM", "Fun to Drive"];

    // Global chart configuration
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.numberFormatter.numberFormat = "#a";
    this.chart.dateFormatter.inputDateFormat = "yyyy-MM-dd";
    this.chart.data = data;
    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.colors.list = SOV_MIX_COLORS.map((chartColor) => color(chartColor));
    // this.chart.legend.marginBottom = 25;
    this.chart.legend.maxHeight = 100;
    this.chart.legend.scrollable = true;
    const legendContainer = create("legenddiv", Container);
    legendContainer.width = percent(100);
    legendContainer.height = percent(100);
    this.chart.legend.parent = legendContainer;
    this.chart.legend.reverseOrder = false;

    // this.chart.legend.disabled = true;

    // Create data chart axes
    const dateAxis = this.chart.xAxes.push(new DateAxis());
    configureDateAxis(dateAxis, theme);
    // Pin the base interval to a month so amCharts doesn't auto-detect a wider
    // interval (e.g. yearly) for sparse/irregular data and round data dates down
    // to Jan 1. Without this, axis tick labels derive their fiscal quarter from a
    // rounded date and drift from the tooltip (e.g. axis "Q4 FY24" vs data "Q3 FY25").
    dateAxis.baseInterval = { timeUnit: "month", count: 1 };
    dateAxis.renderer.cellStartLocation = 0.0;
    dateAxis.renderer.cellEndLocation = 0.0;
    dateAxis.renderer.minGridDistance = 0;
    dateAxis.renderer.labels.template.rotation = -90;
    dateAxis.renderer.labels.template.verticalCenter = "middle";
    dateAxis.renderer.labels.template.horizontalCenter = "left";

    // add logic to display this for more than one year monthly
    if ((diff > 360 && granularity == "monthly") || diff > 720) {
      dateAxis.renderer.labels.template.rotation = -90;
      dateAxis.renderer.labels.template.verticalCenter = "middle";
      dateAxis.renderer.labels.template.horizontalCenter = "left";
    }
    granularity === "monthly" && dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_MonthlyFY);
    granularity === "quarterly" &&
      market !== "China" &&
      market !== "China_Ev" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_QuarterlyFY);
    granularity === "yearly" &&
      market !== "China" &&
      market !== "China_Ev" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_YearlyFY);

    granularity === "quarterly" &&
      (market == "China" || market == "China_Ev") &&
      dateAxis.renderer.labels.template.adapter.add("text", dateChinaAxisLabelFormatter_QuarterlyFY);
    granularity === "yearly" &&
      (market == "China" || market == "China_Ev") &&
      dateAxis.renderer.labels.template.adapter.add("text", dateChinaAxisLabelFormatter_YearlyFY);

    // Creates value axis
    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.min = 0;
    valueAxis.max = maximumScoreRange;
    // valueAxis.extraMax = 0.1;
    if (this.props.percentageChart) {
      // valueAxis.max = 100;
      valueAxis.extraMax = 0;
    }
    valueAxis.numberFormatter.numberFormat = "#.#a";
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return text + (percentageChart ? "%" : "");
    });

    seriesList?.forEach(({ name, field, hide, colour }, idx) => {
      const series = this.chart.series.push(new LineSeries());
      // This is to make sure that the chart is not connected when the data is empty
      // series.connect = false;
      const bullet = lineSeriesConfiguration(series, {
        valueY: `${field}`,
        name: `${name}`,
      });
      series.hiddenInLegend = hide;
      series.xAxis = dateAxis;
      series.dataFields.dateX = "date";
      if (colour) {
        series.fill = color(colour);
        series.stroke = color(colour);
      }
      bullet.adapter.add("tooltipText", (text, target) => {
        const date = target?.dataItem?.dates?.dateX;
        if (granularity === "quarterly") {
          //@ts-ignore
          const dateVal = market == "China" || market == "China_Ev" ? formatChinaDateAxisLabelToQuarterlyFY(date) : formatDateAxisLabelToQuarterlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] ${dateVal} ${name}[/ font-size: var(--regular_font_size);]: {valueY.formatNumber("#,###.0")}${percentageChart ? "%" : ""
            }`;
        } else if (granularity === "yearly") {
          //@ts-ignore
          const dateVal = market == "China" || market == "China_Ev" ? formatChinaDateAxisLabelToYearlyFY(date) : formatDateAxisLabelToYearlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] ${dateVal} ${name}[/ font-size: var(--regular_font_size);]: {valueY.formatNumber("#,###.0")}${percentageChart ? "%" : ""
            }`;
        } else {
          text = `[bold; font-size: var(--regular_font_size);]{dateX.formatDate('MMM YYYY')} ${name} [/ font-size: var(--regular_font_size);]: {valueY.formatNumber("#,###.0")}${percentageChart ? "%" : ""
            }`;
        }

        return text;
      });
    });

    for (let i = 0; i < this.chart.series.length; i++) {
      const chart = this.chart;
      const series = chart.series.getIndex(i);
      series?.events.on("hidden", function (ev) {
        const index = chart.series.indexOf(ev.target);
        const targetSeriesName = ev.target.name;
        console.log(targetSeriesName)
        metricCharts.forEach((metric) => {
          const metricChart = registry.baseSprites.find(function (chartObj) {
            return chartObj?.htmlContainer?.id === metric;
          });
          //@ts-ignore
          if (metricChart?.series) {
            //@ts-ignore
            const matchingSeries = metricChart.series.values.filter(series => series.name === targetSeriesName);
            matchingSeries.forEach((series: LineSeries) => {
              series.hide();
            });
          }
        });
      });

      series?.events.on("shown", function (ev) {
        const index = chart.series.indexOf(ev.target);
        const targetSeriesName = ev.target.name;
        metricCharts.forEach((metric) => {
          const metricChart = registry.baseSprites.find(function (chartObj) {
            return chartObj?.htmlContainer?.id === metric;
          });
          //@ts-ignore
          if (metricChart?.series) {
            //@ts-ignore
            const matchingSeries = metricChart.series.values.filter(series => series.name === targetSeriesName);
            matchingSeries.forEach((series: LineSeries) => {
              series.show();
            });
          }
        });
      });
    }
  }
  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
      </div>
    );
  }
}

export default BrandKPITrendChart;
